import {
  AnimatePresence,
  LayoutGroup,
  motion,
  type Transition,
} from 'framer-motion';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CrownIcon, PlayIcon, SearchIcon } from './icons';

// Extracted style constants to avoid reference changes on re-render
const navBoxShadow =
  '0 20px 50px rgba(0, 0, 0, 0.5), 0 8px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)';
const activePillBoxShadow = '0 4px 15px rgba(139, 92, 246, 0.4)';

interface NavItemConfig {
  id: 'drama' | 'search' | 'premium';
  label: string;
  path: string;
  icon: React.ComponentType<{
    size?: number | string;
    strokeWidth?: number;
    className?: string;
  }>;
}

const tabs: NavItemConfig[] = [
  { id: 'drama', label: 'nav.drama', path: '/', icon: PlayIcon },
  { id: 'search', label: 'nav.search', path: '/search', icon: SearchIcon },
  {
    id: 'premium',
    label: 'nav.premium',
    path: '/purchase',
    icon: CrownIcon,
  },
];

type TabType = NavItemConfig['id'];

// Spring transition presets
const transitions = {
  spring: {
    type: 'spring' as const,
    stiffness: 500,
    damping: 30,
  },
  springBouncy: {
    type: 'spring' as const,
    stiffness: 400,
    damping: 25,
  },
  smooth: {
    type: 'tween' as const,
    duration: 0.2,
    ease: 'easeOut' as const,
  },
};

export const Navigation = memo(function Navigation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const inTelegram =
    typeof window !== 'undefined' &&
    Boolean(
      (window as unknown as { Telegram?: { WebApp?: unknown } }).Telegram
        ?.WebApp
    );

  // Get active tab from current path — only match exact tab routes
  const activeTab: TabType | null =
    tabs.find((tab) => tab.path === location.pathname)?.id ?? null;

  const handleTabChange = (tab: TabType) => {
    if (inTelegram) {
      try {
        const tg = (
          window as unknown as {
            Telegram?: {
              WebApp?: {
                HapticFeedback?: { impactOccurred: (type: string) => void };
              };
            };
          }
        ).Telegram;
        tg?.WebApp?.HapticFeedback?.impactOccurred('light');
      } catch {
        // Ignore haptic feedback errors
      }
    }
    const targetPath = tabs.find((item) => item.id === tab)?.path ?? '/';
    navigate(targetPath);
  };

  return (
    <div className="fixed bottom-6 left-0 right-0 flex justify-center px-6 pointer-events-none z-50">
      <LayoutGroup>
        <motion.nav
          className="pointer-events-auto flex items-center gap-1 rounded-full border border-gray-700/30 bg-[#1c1c1e]/95 backdrop-blur-xl"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={transitions.springBouncy as Transition}
          style={{
            boxShadow: navBoxShadow,
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;

            return (
              <motion.button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`relative flex flex-col items-center justify-center h-16 w-24 rounded-full transition-colors duration-200 ${
                  isActive ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                }`}
                whileHover={{ scale: isActive ? 0.8 : 1.2 }}
                whileTap={{ scale: 0.9 }}
                transition={transitions.spring as Transition}
              >
                {/* Active Background Pill with layoutId for smooth animation */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      layoutId="active-pill"
                      className="absolute inset-0 bg-accent rounded-full"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 35,
                      }}
                      style={{
                        boxShadow: activePillBoxShadow,
                      }}
                    />
                  )}
                </AnimatePresence>

                {/* Content container - Icon top, Label bottom */}
                <div className="relative z-10 flex flex-col items-center justify-center">
                  {/* Icon */}
                  <motion.div
                    animate={{ scale: isActive ? 1.1 : 0.9 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                  </motion.div>

                  {/* Label - always visible below icon */}
                  <span
                    className={`text-[10px] mt-1 ${isActive ? 'opacity-100' : 'opacity-70'} ${isActive ? 'font-extrabold' : 'font-medium'}`}
                  >
                    {t(tab.label)}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </motion.nav>
      </LayoutGroup>
    </div>
  );
});
